import { Injectable } from '@angular/core';
import { Expense, ExpenseNameMap } from '../_models/expense';
import { Table, getTableInputReferences } from '../_models/table';
import { Formula } from '../_models/formula';
import { Package } from '../_models/package';
import { Population } from '../_models/population';
import { AidYearChange, CalcRules } from '../_models/calcPackage';

import { convertTimestampsPipe } from '../_pipes/convert-firestore-timestamp.pipe';

import { Firestore, doc, docData, setDoc, collection, DocumentReference, CollectionReference, addDoc, getDoc } from '@angular/fire/firestore';
import { BehaviorSubject, Observable, Subscription, combineLatest, firstValueFrom } from 'rxjs';
import { tap, map, filter } from 'rxjs/operators';

import { uniqWith, findIndex, isEqual, find } from 'lodash-es';
import { AuthService } from './auth.service';
import { QuestionsService } from './questions.service';
import { UnivPrefsService } from './univ-prefs.service';
import { isAidYearSupported, unsupportedAidYearMessage } from '../_shared/aid-year/aid-year.config';
import { stripUndefinedFields } from './strip-undefined-fields';

export interface UsedBy {
  entityType: 'Population' | 'Table' | 'Formula' | 'Question';
  entityId: string;
  name: string;
  useType: 'Package' | 'Cost' | 'Formula' | 'Table' | 'Population' | 'Next Step';
  disabled?: boolean;
}

export interface RuleInfo {
  lastUpdated: Date | null;
  lastUpdatedBy?: string;
  publishedDate: Date | null;
  aidYear?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CalcConfigService {
  expenses = new BehaviorSubject<Expense[]>([]);
  packages = new BehaviorSubject<Package[]>([]);
  populations = new BehaviorSubject<Population[]>([]);
  tables = new BehaviorSubject<Table[]>([]);
  formulas = new BehaviorSubject<Formula[]>([]);
  aidYearChanges = new BehaviorSubject<AidYearChange[] | null>(null);
  loadedSchoolId = '';
  loadedDraftSchoolId = '';
  loadedPublishedSchoolId = '';

  draftRules = new BehaviorSubject<CalcRules | null>(null);
  publishedRules = new BehaviorSubject<CalcRules | null>(null);
  /** True = IM school, false = FM school, undefined while draft rules are loading. */
  readonly institutionalMethod$ = this.draftRules.pipe(
    map(rules => rules?.schoolProperties?.institutionalMethod)
  );
  draftSubscription?: Subscription;
  publishSubscription?: Subscription;
  draftRuleInfo: Observable<RuleInfo>;
  publishedRuleInfo: Observable<RuleInfo>;

  usedBy = new BehaviorSubject<UsedBy[]>([]);

  constructor(
    private ups: UnivPrefsService,
    private firestore: Firestore,
    private auth: AuthService,
    private qs: QuestionsService,
  ) {
    this.draftRuleInfo = combineLatest([this.draftRules, this.qs.draftLastUpdated]).pipe(
      filter((pair): pair is [CalcRules, Date | null] => pair[0] != null),
      convertTimestampsPipe(),
      map(([rules, questionsLastUpdated]) => this.toRuleInfo(rules, questionsLastUpdated, null))
    );

    this.publishedRuleInfo = this.publishedRules.pipe(
      filter((rules): rules is CalcRules => rules != null),
      convertTimestampsPipe(),
      map(rules => this.toRuleInfo(rules, null, rules.publishedDate ?? null))
    );
  }

  private toRuleInfo(
    rules: CalcRules,
    questionsLastUpdated: Date | null,
    publishedDate: Date | null
  ): RuleInfo {
    const rulesLastUpdated = rules.lastUpdated ?? publishedDate ?? null;
    const lastUpdated = questionsLastUpdated && rulesLastUpdated
      ? new Date(Math.max(questionsLastUpdated.getTime(), rulesLastUpdated.getTime()))
      : rulesLastUpdated ?? questionsLastUpdated ?? null;
    return {
      publishedDate,
      lastUpdated,
      lastUpdatedBy: rules.lastUpdatedBy,
      aidYear: rules.schoolProperties?.aidYear
    };
  }


  //#region Load collections
  loadSchool(schoolId: string) {
    if (schoolId !== this.loadedSchoolId) {
      this.populations.next([]);
      this.expenses.next([]);
      this.packages.next([]);
      this.tables.next([]);
      this.formulas.next([]);
      this.usedBy.next([]);
      this.loadDraftCalcRules(schoolId);
    }
  }

  loadPublishedCalcRules(schoolId: string) {
    if (schoolId === this.loadedPublishedSchoolId) { return; }

    if (this.publishSubscription) { this.publishSubscription.unsubscribe(); }

    const docRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/published`) as DocumentReference<CalcRules>;
    this.publishSubscription = docData(docRef).pipe(
      tap(res => {
        if (res) {
          console.log('Published Rule Set loaded (or-reloaded)', schoolId);
          this.publishedRules.next(res);
          this.loadedPublishedSchoolId = schoolId;
        } else {
          console.log('No Published Rule Set exists for', schoolId);
        }
      })
    ).subscribe();

  }

  loadDraftCalcRules(schoolId: string, forceReload = false) {
    if (schoolId === this.loadedDraftSchoolId && !forceReload) { return; }

    if (this.draftSubscription) { this.draftSubscription.unsubscribe(); }

    const docRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/draft`) as DocumentReference<CalcRules>;
    this.draftSubscription = docData(docRef).pipe(
      tap(res => {
        if (res) {
          const aidYear = res.schoolProperties?.aidYear;
          if (aidYear != null && !isAidYearSupported(aidYear)) {
            console.error(unsupportedAidYearMessage(aidYear));
          }
          console.log('Draft Rule Set for aid year', aidYear, 'loaded (or-reloaded)', schoolId, JSON.stringify(res).length.toLocaleString('en'));
          this.draftRules.next(res);

          this.tables.next(res.tables || []);
          this.expenses.next(res.expenses || []);
          this.populations.next(res.populations || []);
          this.formulas.next(res.formulas || []);
          this.loadedDraftSchoolId = schoolId;
          this.loadedSchoolId = schoolId;
          this.packages.next(res.packages || []);
          this.mapFormulaObject();
          this.mapTableObject();
          this.mapPopulations();
          this.createTags();

          if (aidYear != null) {
            this.qs.loadQuestions(schoolId, aidYear);
            this.loadAidYearChanges(schoolId, aidYear);
          }
        } else {
          console.log('No Draft Rule Set exists for', schoolId);
        }
      })
    ).subscribe();


  }

  private async loadAidYearChanges(schoolId: string, aidYear: number) {
    this.aidYearChanges.next(null);
    if (!isAidYearSupported(aidYear)) {
      return;
    }

    const docRef = doc(this.firestore, `calcConfig/${schoolId}/aidYearChanges/${aidYear}`) as DocumentReference<{
      aidYearChanges: AidYearChange[];
      lastUpdated: Date;
    }>;
    const changes = await firstValueFrom(docData(docRef).pipe(convertTimestampsPipe()));
    if (changes) {
      this.aidYearChanges.next(changes.aidYearChanges);
    }
  }

  // Attach populations to expenses and packages
  private mapPopulations() {
    const pops = this.populations.value;
    if (pops && pops.length > 0) {

      if (this.expenses.value.length > 0) {
        // Tie populations to expense rules
        for (const cost of this.expenses.value) {
          for (const rule of cost.rules) {
            if (rule.populationId !== '1') {
              const index = findIndex(pops, ['id', rule.populationId]);
              if (index !== -1) {
                // Attach population to expense rule
                rule.population = pops[index];
              } else {
                console.log('Population ID: ', rule.populationId, ' for expense ', cost.name, ' not found');
              }
            }
          }
        }
      }

      if (this.packages.value.length > 0) {
        // Tie populations to package rules
        for (const pack of this.packages.value) {
          for (const rule of pack.rules) {
            if (rule.populationId !== '1') {
              const index = findIndex(pops, ['id', rule.populationId]);
              if (index !== -1) {
                // Attach population to package rule
                rule.population = pops[index];
              } else {
                console.log('Population ID: ', rule.populationId, ' for package ', pack.id, ' not found');
              }
            }
          }
        }
      }
      // this.tagPopulations();
    }
  }

  private mapTableObject() {
    // Check if datasets have been loaded and have items
    if (this.tables.value.length > 0) {
      if (this.expenses.value.length > 0) {
        for (const e of this.expenses.value) {
          for (const r of e.rules) {
            if (r.tableId) {
              r.table = this.getTable(r.tableId);
              if (!r.table) { console.log('Table ID:', r.tableId, 'not found for expense:', e.name); }
            }
          }
        }
      }
      if (this.packages.value.length > 0) {
        for (const p of this.packages.value) {
          for (const r of p.rules) {
            if (r.tableId) {
              r.table = this.getTable(r.tableId);
              if (!r.table) { console.log('Table ID:', r.tableId, 'not found for package:', p.id); }
            }
          }
        }
      }
    }
  }

  private mapFormulaObject() {
    // Check if datasets have been loaded and have items
    if (this.formulas.value.length > 0) {
      if (this.packages.value.length > 0) {
        for (const p of this.packages.value) {
          for (const r of p.rules) {
            if (r.formulaId) {
              this.attachFormula(r, () => {
                console.log('Formula ID:', r.formulaId, 'not found for package:', p.id);
              });
            }
          }
        }
      }
      if (this.populations.value.length > 0) {
        for (const pop of this.populations.value) {
          for (const block of pop.blocks) {
            for (const cond of block.conditions) {
              if (cond.formulaId) {
                this.attachFormula(cond, () => {
                  console.log('Formula ID:', cond.formulaId, 'not found for population:', pop.name);
                });
              }
            }
          }
        }
      }
      if (this.tables.value.length > 0) {
        for (const t of this.tables.value) {
          if (t.formulaId) {
            this.attachFormula(t, () => {
              console.log('Formula ID:', t.formulaId, 'not found for table:', t.name);
            });
          }
        }
      }
    }
  }
  //#endregion

  //#region SAVING
  // Strip attached objects before persisting arrays on the draft calcRules document
  saveTable(schoolId: string, table: Table) {
    const tables = this.tables.value;
    const tableIndex = findIndex(tables, { 'tableId': table.tableId });
    if (tableIndex === -1) {
      tables.push(table);
    } else {
      tables[tableIndex] = table;
    }
    tables.forEach(t => this.stripTableRuntimeFields(t));
    this.saveDraftRules(schoolId, { 'tables': tables, 'tablesLastModified': new Date() });
  }

  saveFormula(schoolId: string, formula: Formula) {
    const formulas = this.formulas.value;
    const formulaIndex = findIndex(formulas, { 'formulaId': formula.formulaId });
    if (formulaIndex === -1) {
      formulas.push(formula);
    } else {
      formulas[formulaIndex] = formula;
    }
    this.saveDraftRules(schoolId, { 'formulas': formulas, 'formulasLastModified': new Date() });
  }

  savePopulation(schoolId: string, population: Population) {
    const populations = this.populations.value;
    const popIndex = findIndex(populations, { 'id': population.id });
    if (popIndex === -1) {
      populations.push(population);
    } else {
      populations[popIndex] = population;
    }
    populations.forEach(pop => this.stripPopulationRuntimeFields(pop));
    this.saveDraftRules(schoolId, { 'populations': populations, 'populationsLastModified': new Date() });
  }

  saveExpenses(schoolId: string, expenses: Expense[]) {
    expenses.forEach(expense => {
      expense.rules.forEach(rule => {
        delete rule.population;
        delete rule.table;
      });
    });
    this.saveDraftRules(schoolId, { 'expenses': expenses, 'expensesLastModified': new Date() });
  }

  savePackages(schoolId: string, packages: Package[]) {
    packages.forEach(pack => {
      pack.rules.forEach(rule => {
        delete rule.population;
        delete rule.table;
        delete rule.formula;
      });
    });
    packages = packages.sort((n1, n2) => n1.order - n2.order);
    this.saveDraftRules(schoolId, { 'packages': packages, 'packagesLastModified': new Date() });
  }

  async saveDraftRules(schoolId: string, updateObject: Partial<CalcRules>) {
    const user = await this.auth.currentUser();
    if (!user) {
      console.error('Cannot save draft rules while signed out');
      alert('Cannot save while signed out. Please sign in and try again.');
      return;
    }

    updateObject.lastUpdated = new Date();
    updateObject.lastUpdatedBy = user.uid;
    // Firestore rejects `undefined`; missing formulas and optional table fields can land on the
    // in-memory arrays after load/edit. Strip them before writing the draft document.
    stripUndefinedFields(updateObject);
    const docRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/draft`) as DocumentReference<CalcRules>;
    setDoc(docRef, updateObject, { merge: true });

  }

  deleteTable(schoolId: string, table: Table) {
    const tables = this.tables.value;
    const tableIndex = findIndex(tables, { 'tableId': table.tableId });
    if (tableIndex !== -1) {
      tables.splice(tableIndex, 1);
      tables.forEach(t => this.stripTableRuntimeFields(t));
      this.saveDraftRules(schoolId, { 'tables': tables });
    }
  }

  deleteFormula(schoolId: string, formula: Formula) {
    const formulas = this.formulas.value;
    const formulaIndex = findIndex(formulas, { 'formulaId': formula.formulaId });
    if (formulaIndex !== -1) {
      formulas.splice(formulaIndex, 1);
      this.saveDraftRules(schoolId, { 'formulas': formulas });
    }
  }

  deletePopulation(schoolId: string, pop: Population) {
    const pops = this.populations.value;
    const popIndex = findIndex(pops, { 'id': pop.id });
    if (popIndex !== -1) {
      pops.splice(popIndex, 1);
      pops.forEach(pop => this.stripPopulationRuntimeFields(pop));
      this.saveDraftRules(schoolId, { 'populations': pops });
    }
  }

  async publishRules(schoolId: string): Promise<string> {
    // Store a backup copy of the rules
    const pubRules = this.publishedRules.value;
    if (pubRules) {
      pubRules.publishedDate = pubRules.publishedDate || new Date();
      // TODO: Wrap try catches around each database write and return error message to publishRules caller.
      try {
        const backupColRef = collection(this.firestore, `calcConfig_backup/${schoolId}/calcRules`) as CollectionReference<CalcRules>;
        await addDoc(backupColRef, pubRules);
      } catch (err) {
        return 'Contact Support - Rules not published: could not backup Calculation Rules ' + err;
      }
    }

    // Copy draft rules to published rules - get a fresj copy of the draft rules
    const draftDocRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/draft`) as DocumentReference<CalcRules>;
    const draftDoc = await getDoc(draftDocRef);
    const rules = draftDoc.data();
    if (!rules) {
      return 'Contact Support - Rules not published: no draft rules found';
    }
    const aidYear = rules.schoolProperties?.aidYear;
    if (aidYear == null) {
      return 'Contact Support - Rules not published: draft rules are missing an aid year';
    }
    const size = JSON.stringify(rules).length.toLocaleString('en');
    console.log('pub size', size);
    rules.publishedDate = new Date();

    try {
      const publishedDocRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/published`);
      await setDoc(publishedDocRef, rules);
      // Log last published date and set the published aid year
      const schoolDocRef = doc(this.firestore, 'schools', schoolId);
      await setDoc(schoolDocRef, { lastPublished: new Date(Date.now()), schoolProperties: { aidYear: aidYear } }, { merge: true });
      // Update the school preferences with the aid year
      const stringAidYear = `20${aidYear.toString().slice(0, 2)}-20${aidYear.toString().slice(2)}`;
      this.ups.savePreferences({ aidYear: stringAidYear }, false);
    } catch (err) {
      return 'Contact Support - Rules not published: could not publish rules (size ' + size + ') ' + err;
    }

    return 'Rules succesfully published';
  }

  async resetRules(schoolId: string) {
    const pubRules = this.publishedRules.value;
    if (pubRules) {
      // Make a backup copy of the draft rules (should have the lastest date updated in hte backup folder )
      const draftRules = this.draftRules.value;
      if (draftRules) {
        const backupColRef = collection(this.firestore, `calcConfig_backup/${schoolId}/calcRules`) as CollectionReference<CalcRules>;
        await addDoc(backupColRef, draftRules);
      }

      const draftDocRef = doc(this.firestore, `calcConfig/${schoolId}/calcRules/draft`) as DocumentReference<CalcRules>;
      await setDoc(draftDocRef, pubRules);
    }
  }

  //#endregion


  private createTags() {
    const usedByList: UsedBy[] = [];
    for (const cost of this.expenses.value) {
      for (const rule of cost.rules) {
        if (rule.populationId !== '1') {
          usedByList.push({ useType: 'Cost', name: ExpenseNameMap[cost.type], entityType: 'Population', entityId: rule.populationId });
        }
        if (rule.tableId) {
          usedByList.push({ useType: 'Cost', name: ExpenseNameMap[cost.type], entityType: 'Table', entityId: rule.tableId });
        }
      }
    }

    for (const pack of this.packages.value) {
      for (const rule of pack.rules) {
        if (rule.populationId !== '1') {
          // TODO: Use package "name map"
          usedByList.push({
            useType: 'Package', name: pack.id, entityType: 'Population',
            entityId: rule.populationId, disabled: !pack.enabled
          });
        }
        if (rule.tableId) {
          usedByList.push({ useType: 'Package', name: pack.id, entityType: 'Table', entityId: rule.tableId, disabled: !pack.enabled });
        }
        if (rule.type === 'Formula' && rule.formulaId) {
          usedByList.push({ useType: 'Package', name: pack.id, entityType: 'Formula', entityId: rule.formulaId, disabled: !pack.enabled });
        }
        if (rule.type === 'Question' && rule.keyId) {
          usedByList.push({ useType: 'Package', name: pack.id, entityType: 'Question', entityId: rule.keyId, disabled: !pack.enabled });
        }
      }
    }

    for (const pop of this.populations.value) {
      if (pop.nextStep) {
        usedByList.push({ useType: 'Next Step', name: 'Next Step', entityType: 'Population', entityId: pop.id });
      }
      for (const block of pop.blocks) {
        for (const cond of block.conditions) {
          if (cond.type === 'Formula' && cond.formulaId) {
            usedByList.push({ useType: 'Population', name: pop.name, entityType: 'Formula', entityId: cond.formulaId });
          }
          if (cond.type === 'Question' && cond.keyId) {
            usedByList.push({ useType: 'Population', name: pop.name, entityType: 'Question', entityId: cond.keyId });
          }
        }
      }
    }


    for (const formula of this.formulas.value) {
      const formulaSplit = formula.formula.split('|');
      for (const sub of formulaSplit) {
        const prefix = sub.split('.')[0];
        if (prefix === 'tab') {
          const tableId = sub.split('.')[1];
          usedByList.push({ useType: 'Formula', name: formula.name, entityType: 'Table', entityId: tableId });
        }
        if (prefix === 'student' || prefix === 'parent') {
          usedByList.push({ useType: 'Formula', name: formula.name, entityType: 'Question', entityId: sub });
        }
      }
    }


    for (const table of this.tables.value) {
      for (const ref of getTableInputReferences(table)) {
        usedByList.push({ useType: 'Table', name: table.name, entityType: ref.entityType, entityId: ref.entityId });
      }
    }

    this.usedBy.next(uniqWith(usedByList, isEqual));
  }

  getFormula(formulaId: string): Formula | undefined {
    return find(this.formulas.value, { 'formulaId': formulaId });
  }

  private attachFormula(owner: { formulaId?: string; formula?: Formula }, onMissing: () => void): void {
    const formula = owner.formulaId ? this.getFormula(owner.formulaId) : undefined;
    if (formula) {
      owner.formula = formula;
      return;
    }
    delete owner.formula;
    onMissing();
  }

  private stripPopulationRuntimeFields(population: Population): void {
    population.blocks.forEach(block => {
      block.conditions.forEach(cond => {
        delete cond.question;
        delete cond.formula;
      });
    });
  }

  private stripTableRuntimeFields(table: Table): void {
    delete table.formula;
    delete table.question;
    if (table.matrixTable) {
      delete table.matrixTable.rows.formula;
      delete table.matrixTable.columns.formula;
    }
  }

  getTable(tableId: string): Table | undefined {
    return find(this.tables.value, { 'tableId': tableId });
  }

  getUniqueId(): string {
    // This is the new way to generate a client-side ID in modular API
    return doc(collection(this.firestore, '_temp')).id;
  }

}